<script setup>
import { ref, computed } from 'vue'
import { Head, router, useForm, usePage } from '@inertiajs/vue3'
import {
    PhArrowLeft, PhPrinter, PhGraduationCap,
    PhPencilSimple, PhCheckCircle,
} from '@phosphor-icons/vue'
import Layout   from '@pages/Shared/layouts/AdminLayout.vue'
import Modal    from '@pages/Shared/Modal.vue'
import Input    from '@pages/Shared/form/Input.vue'
import Select   from '@pages/Shared/form/Select.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import Spinner  from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from '@/toast.js'
import { useAdmissionStore } from '@/Stores/useAdmissionStore'

const props = defineProps({
    admission: Object,
})

const page           = usePage()
const can            = computed(() => page.props.can || {})
const admissionStore = useAdmissionStore()

const adm       = computed(() => props.admission)
const applicant = computed(() => adm.value.application?.applicant ?? null)

const fullName = computed(() => {
    if (adm.value.type === 'online' && applicant.value) {
        return [applicant.value.first_name, applicant.value.other_name, applicant.value.last_name]
            .filter(Boolean).join(' ')
    }
    return [adm.value.first_name, adm.value.other_name, adm.value.last_name]
        .filter(Boolean).join(' ')
})

// ── Enroll ─────────────────────────────────────────────────────────────────
const enrollModal = ref(false)
const enrollForm  = useForm({ student_id: '' })

const submitEnroll = () => {
    enrollForm.post(`/admin/admissions/${adm.value.id}/enroll`, {
        onSuccess: page => {
            enrollModal.value = false
            enrollForm.reset()
            flashToast(page.props.flash)
        },
    })
}

// ── Edit (manual only) ─────────────────────────────────────────────────────
const editModal = ref(false)
const editForm  = useForm({
    first_name:            adm.value.first_name,
    last_name:             adm.value.last_name,
    other_name:            adm.value.other_name,
    date_of_birth:         adm.value.date_of_birth,
    gender:                adm.value.gender,
    phone:                 adm.value.phone,
    email:                 adm.value.email,
    address:               adm.value.address,
    guardian_name:         adm.value.guardian_name,
    guardian_phone:        adm.value.guardian_phone,
    guardian_relationship: adm.value.guardian_relationship,
    notes:                 adm.value.notes,
})

const submitEdit = () => {
    editForm.put(`/admin/admissions/${adm.value.id}`, {
        onSuccess: page => {
            editModal.value = false
            flashToast(page.props.flash)
        },
    })
}
</script>

<template>
    <Layout>
        <Head :title="`Admission — ${fullName}`" />

        <!-- ── Enroll Modal ── -->
        <Modal v-model="enrollModal" title="Enroll as Student" @close="enrollModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitEnroll">
                <p class="text-sm text-gray-600 mb-4">
                    Enrolling <strong>{{ fullName }}</strong>.
                    Leave Student ID blank to use the admission number
                    <strong>({{ adm.admission_number }})</strong>.
                </p>
                <Input
                    label="Custom Student ID (optional)"
                    v-model="enrollForm.student_id"
                    placeholder="e.g. 25/HD/00301"
                    :error="enrollForm.errors.student_id"
                    class="mb-4"
                />
                <button type="submit" :disabled="enrollForm.processing"
                    class="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="enrollForm.processing" :size="14" />
                    Confirm Enrollment
                </button>
            </form>
        </Modal>

        <!-- ── Edit Modal (manual only) ── -->
        <Modal v-model="editModal" title="Edit Admission" @close="editModal = false">
            <form class="p-4 md:p-5 space-y-4 max-h-[75vh] overflow-y-auto" @submit.prevent="submitEdit">
                <div class="grid grid-cols-2 gap-4">
                    <Input label="First Name"    v-model="editForm.first_name"    :error="editForm.errors.first_name" />
                    <Input label="Last Name"     v-model="editForm.last_name"     :error="editForm.errors.last_name" />
                    <Input label="Other Name"    v-model="editForm.other_name" />
                    <Input label="Date of Birth" v-model="editForm.date_of_birth" type="date" />
                    <Select label="Gender" v-model="editForm.gender"
                        :options="[{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }]"
                        placeholder="Select gender" />
                    <Input label="Phone" v-model="editForm.phone" />
                    <Input label="Email" v-model="editForm.email" type="email" />
                </div>
                <Textarea label="Address"  v-model="editForm.address"  :rows="2" />
                <div class="grid grid-cols-2 gap-4">
                    <Input label="Guardian Name"    v-model="editForm.guardian_name" />
                    <Input label="Guardian Phone"   v-model="editForm.guardian_phone" />
                    <Input label="Relationship"     v-model="editForm.guardian_relationship" />
                </div>
                <Textarea label="Notes" v-model="editForm.notes" :rows="2" />
                <button type="submit" :disabled="editForm.processing"
                    class="w-full py-3 rounded-lg bg-primary hover:bg-primary-light text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="editForm.processing" :size="14" />
                    Save Changes
                </button>
            </form>
        </Modal>

        <!-- ── Back + Header ── -->
        <div class="flex items-center gap-3 mb-6">
            <button @click="router.get('/admin/admissions')"
                class="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition">
                <PhArrowLeft :size="20" />
            </button>
            <div class="flex-1">
                <h2 class="text-xl font-bold text-primary">Admission Detail</h2>
                <p class="text-sm text-gray-500">{{ adm.admission_number }}</p>
            </div>

            <div class="flex items-center gap-2">
                <!-- Edit (manual only, before enrollment) -->
                <button
                    v-if="adm.type === 'manual' && !adm.student_id"
                    @click="editModal = true"
                    class="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200
                           hover:bg-gray-50 text-gray-700 text-sm font-medium transition">
                    <PhPencilSimple :size="16" />
                    Edit
                </button>

                <!-- Print — store-based, no page navigation -->
                <button
                    @click="admissionStore.printAdmissionLetter(adm)"
                    :disabled="admissionStore.isGenerating"
                    class="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200
                           hover:bg-gray-50 text-gray-700 text-sm font-medium transition
                           disabled:opacity-50 disabled:cursor-not-allowed">
                    <Spinner v-if="admissionStore.isGenerating" :size="14" />
                    <PhPrinter v-else :size="16" />
                    {{ admissionStore.isGenerating ? 'Generating…' : 'Print' }}
                </button>

                <!-- Enroll -->
                <button
                    v-if="!adm.student_id"
                    @click="enrollModal = true"
                    class="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600
                           hover:bg-emerald-700 text-white text-sm font-semibold transition">
                    <PhGraduationCap :size="16" />
                    Enroll
                </button>
                <span v-else
                    class="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50
                           text-emerald-700 text-sm font-medium">
                    <PhCheckCircle :size="16" />
                    Enrolled
                </span>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <!-- ── Left: Personal info ── -->
            <div class="lg:col-span-1 space-y-4">
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
                    <div class="flex items-center justify-between">
                        <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Personal</h4>
                        <span class="text-xs px-2 py-0.5 rounded-full capitalize font-medium"
                            :class="adm.type === 'online'
                                ? 'bg-primary-50 text-primary-700'
                                : 'bg-indigo-50 text-indigo-700'">
                            {{ adm.type }}
                        </span>
                    </div>
                    <p class="font-semibold text-gray-900 text-base">{{ fullName }}</p>
                    <div class="text-sm text-gray-600 space-y-1.5">
                        <p>
                            <span class="text-gray-400">Admission No:</span>
                            <span class="font-mono ml-1">{{ adm.admission_number }}</span>
                        </p>
                        <p><span class="text-gray-400">Session:</span> {{ adm.academic_session?.name }}</p>
                        <p><span class="text-gray-400">Class:</span> {{ adm.class_room?.name }}</p>
                        <p class="capitalize">
                            <span class="text-gray-400">Gender:</span>
                            {{ (adm.type === 'online' ? applicant?.gender : adm.gender) ?? '—' }}
                        </p>
                        <p>
                            <span class="text-gray-400">Phone:</span>
                            {{ (adm.type === 'online' ? applicant?.phone : adm.phone) ?? '—' }}
                        </p>
                        <p>
                            <span class="text-gray-400">Email:</span>
                            {{ (adm.type === 'online' ? applicant?.email : adm.email) ?? '—' }}
                        </p>
                        <p>
                            <span class="text-gray-400">Admitted by:</span>
                            {{ adm.admitted_by_staff?.name ?? '—' }}
                        </p>
                        <p>
                            <span class="text-gray-400">Admitted at:</span>
                            {{ adm.admitted_at ? new Date(adm.admitted_at).toLocaleDateString() : '—' }}
                        </p>
                    </div>
                </div>

                <!-- Enrollment info -->
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-2">
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Enrollment</h4>
                    <div class="text-sm text-gray-600 space-y-1.5">
                        <p>
                            <span class="text-gray-400">Student ID:</span>
                            <span class="font-mono ml-1">{{ adm.student_id ?? '—' }}</span>
                        </p>
                        <p>
                            <span class="text-gray-400">Enrolled at:</span>
                            {{ adm.enrolled_at ? new Date(adm.enrolled_at).toLocaleDateString() : '—' }}
                        </p>
                        <p>
                            <span class="text-gray-400">Print status:</span>
                            <span :class="adm.is_printed ? 'text-green-600' : 'text-gray-400'">
                                {{ adm.is_printed
                                    ? `Printed ${new Date(adm.printed_at).toLocaleDateString()}`
                                    : 'Not printed' }}
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            <!-- ── Right: Payments + Notes + Link to application ── -->
            <div class="lg:col-span-2 space-y-5">

                <!-- Online application link -->
                <div v-if="adm.type === 'online' && adm.application"
                    class="bg-primary-50 border border-primary-100 rounded-xl p-4 flex items-center justify-between">
                    <div>
                        <p class="text-sm font-medium text-primary-800">Online Application</p>
                        <p class="text-xs text-primary-600 mt-0.5">
                            Submitted: {{ adm.application.submitted_at
                                ? new Date(adm.application.submitted_at).toLocaleDateString()
                                : '—' }}
                        </p>
                    </div>
                    <button
                        @click="router.get(`/admin/applications/${adm.application.id}`)"
                        class="px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white
                               text-xs font-semibold transition">
                        View Application
                    </button>
                </div>

                <!-- Payments -->
                <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h4 class="font-semibold text-gray-800">Payments</h4>
                        <button
                            @click="router.get(`/admin/payments?admission_id=${adm.id}`)"
                            class="text-xs text-primary font-medium hover:underline">
                            Record Payment
                        </button>
                    </div>
                    <div v-if="adm.payments?.length" class="divide-y divide-gray-100">
                        <div v-for="payment in adm.payments" :key="payment.id"
                            class="px-5 py-4 flex items-center justify-between text-sm">
                            <div>
                                <p class="font-medium text-gray-800 capitalize">
                                    {{ payment.category?.replace(/_/g, ' ') }}
                                </p>
                                <p class="text-xs text-gray-400">
                                    {{ payment.reference }}
                                    · {{ payment.payment_method }}
                                    · {{ payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : '' }}
                                </p>
                            </div>
                            <div class="text-right">
                                <p class="font-semibold text-gray-900">
                                    ₦{{ Number(payment.amount_paid).toLocaleString() }}
                                </p>
                                <span class="text-xs capitalize"
                                    :class="{
                                        'text-green-600':  payment.status === 'paid',
                                        'text-yellow-600': payment.status === 'pending',
                                        'text-red-500':    payment.status === 'failed',
                                        'text-gray-500':   payment.status === 'waived',
                                    }">
                                    {{ payment.status }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-5 py-8 text-center text-sm text-gray-400">
                        No payments recorded yet.
                    </div>
                </div>

                <!-- Notes -->
                <div v-if="adm.notes" class="bg-amber-50 border border-amber-100 rounded-xl p-5">
                    <h4 class="text-sm font-semibold text-amber-700 mb-2">Notes</h4>
                    <p class="text-sm text-amber-800">{{ adm.notes }}</p>
                </div>
            </div>
        </div>
    </Layout>
</template>